import { api } from "@/lib/api";
import type {
  AddSeriesRequest,
  BulkSetSeriesTypeRequest,
  BulkSetSeriesTypeResponse,
  Episode,
  GrabRecord,
  Season,
  Series,
  SeriesListItem,
  SeriesLookupResult,
  SeriesWithSeasons,
  UpdateEpisodeRequest,
  UpdateSeasonRequest,
  UpdateSeriesRequest,
} from "./types";

/** A monitored episode without a file — drives the Wanted (TV) tab. */
export interface WantedEpisode {
  episode_id: number;
  series_id: number;
  series_title: string;
  series_poster_url: string | null;
  season_number: number;
  episode_number: number;
  title: string | null;
  overview: string | null;
  air_date: string | null;
  runtime_minutes: number | null;
}

export interface TvScanStatus {
  running: boolean;
  last_scan_at: string | null;
  unmatched_count: number;
}

export interface TvUnmatchedFile {
  id: number;
  path: string;
  reason: string;
  scanned_at: string;
}

/**
 * Per-episode rename outcome (issue #70). Surfaced by both the
 * single-episode `POST /api/v1/episodes/:id/rename` endpoint
 * (`status: "renamed" | "unchanged"`) and as a row inside the
 * `SeriesRenameResponse.outcomes` array (which can additionally be
 * `skipped` or `failed`).
 */
export type EpisodeRenameStatus =
  | "renamed"
  | "unchanged"
  | "skipped"
  | "failed";

export interface EpisodeRenameResponse {
  episode_id: number;
  new_path: string;
  status: EpisodeRenameStatus;
}

/** One row in `SeriesRenameResponse.outcomes`. */
export interface SeriesRenameOutcome {
  episode_id: number;
  season_number: number;
  episode_number: number;
  new_path: string | null;
  status: EpisodeRenameStatus;
  message: string | null;
}

export interface SeriesRenameResponse {
  considered: number;
  renamed: number;
  unchanged: number;
  failed: number;
  outcomes: SeriesRenameOutcome[];
}

export interface TvSettingsResponse {
  library_path: string | null;
  library_path_source: "db" | "env" | null;
  scan_interval_hours: number;
  last_scan_at: string | null;
  refresh_interval_hours: number;
  last_refresh_at: string | null;
  /** Per-episode air-date gate (issue #64). `0` = search at air time. */
  episode_grab_lead_hours: number;
  /**
   * Issue #66: global default for the per-series "monitor specials"
   * toggle. When `true`, newly-added series default to monitoring S00
   * episodes (still respecting the per-series override). Default
   * `false` matches Sonarr.
   */
  monitor_specials_default: boolean;
}

export const tvApi = {
  list: () => api<SeriesListItem[]>("/api/v1/series"),
  get: (id: number) => api<SeriesWithSeasons>(`/api/v1/series/${id}`),
  lookup: (q: string) =>
    api<SeriesLookupResult[]>(
      `/api/v1/series/lookup?q=${encodeURIComponent(q)}`,
    ),
  add: (body: AddSeriesRequest) =>
    api<Series>("/api/v1/series", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: number, body: UpdateSeriesRequest) =>
    api<Series>(`/api/v1/series/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api<void>(`/api/v1/series/${id}`, { method: "DELETE" }),
  episodes: (seriesId: number) =>
    api<Episode[]>(`/api/v1/series/${seriesId}/episodes`),
  updateEpisode: (episodeId: number, body: UpdateEpisodeRequest) =>
    api<Episode>(`/api/v1/episodes/${episodeId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  updateSeason: (seasonId: number, body: UpdateSeasonRequest) =>
    api<Season>(`/api/v1/seasons/${seasonId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  searchEpisode: (episodeId: number) =>
    api<GrabRecord>(`/api/v1/episodes/${episodeId}/search`, { method: "POST" }),

  /**
   * Rename a single episode's on-disk file to the canonical
   * `{Series} - SxxEyy - {Title}.{ext}` template. 422 when has_file=0
   * or the recorded path is missing on disk; 409 when the canonical
   * destination is already taken by another file.
   */
  renameEpisode: (episodeId: number) =>
    api<EpisodeRenameResponse>(`/api/v1/episodes/${episodeId}/rename`, {
      method: "POST",
    }),

  /**
   * Sweep every `has_file=1` episode for the series and rename to the
   * canonical filename. Bounded concurrency on the backend (3) — safe
   * to fire on a 200-episode series.
   */
  renameSeries: (seriesId: number) =>
    api<SeriesRenameResponse>(`/api/v1/series/${seriesId}/rename-all`, {
      method: "POST",
    }),

  /**
   * Issue #65: bulk-set `series_type` across many series in one
   * request. Used by the "Set type for all" admin tool on `/tv`.
   */
  bulkSetSeriesType: (body: BulkSetSeriesTypeRequest) =>
    api<BulkSetSeriesTypeResponse>("/api/v1/tv/series-type/bulk", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Monitored episodes (in monitored series) without a file. */
  wanted: (limit: number = 200, seriesId?: number) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (seriesId !== undefined) params.set("series_id", String(seriesId));
    return api<WantedEpisode[]>(`/api/v1/tv/wanted?${params.toString()}`);
  },

  scan: {
    start: () =>
      api<{ ok: boolean }>("/api/v1/tv/scan", { method: "POST" }),
    status: () => api<TvScanStatus>("/api/v1/tv/scan/status"),
    unmatched: (limit: number = 200) =>
      api<TvUnmatchedFile[]>(
        `/api/v1/tv/scan/unmatched?limit=${limit}`,
      ),
    claim: (unmatchedId: number, body: { episode_id: number }) =>
      api<void>(`/api/v1/tv/scan/unmatched/${unmatchedId}/claim`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  settings: {
    get: () => api<TvSettingsResponse>("/api/v1/settings/tv"),
    update: (body: {
      library_path?: string;
      scan_interval_hours?: number;
      refresh_interval_hours?: number;
      episode_grab_lead_hours?: number;
      /** Issue #66: global default for the specials toggle. */
      monitor_specials_default?: boolean;
    }) =>
      api<TvSettingsResponse>("/api/v1/settings/tv", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },
};
